import { Card, SectionLabel } from '../../../design/components'
import { AbilitiesCard } from '../../characters/sections/AbilitiesCard'
import { EquipmentCard } from '../../characters/sections/EquipmentCard'
import { HarmCard } from '../../characters/sections/HarmCard'
import {
  EnchantedItemsCard,
  FocusItemsCard,
  RoteSpellsCard,
  SpecializationsCard,
} from '../../characters/sections/MagicCards'
import { SkillsCard } from '../../characters/sections/SkillsCard'
import { Person } from '../../../design/components'
import styles from '../../scenes/sections/play.module.css'
import type { EncounterType } from '../../live/types'
import type { EncounterCard } from '../types'

/**
 * One participant's combat card — the character sheet seen through a combat lens.
 *
 * **Stage 4's renderers draw it, unchanged.** The card's character halves are the Phase 2
 * section DTOs *verbatim* — the same `SkillsSectionDto`, `StressSectionDto` and the rest
 * the character sheet reads — so the sheet's components take them directly. That is the
 * implementation plan's "reuse Stage 4's section renderers wholesale", and it is what
 * makes it impossible for a card and a sheet to disagree about a stress track.
 *
 * They are mounted without an `editing` bundle, which every one of them already treats as
 * read-only. Marking stress belongs on the character's own sheet, where one envelope
 * writes one cache entry; a second editable copy here would be a second place for the
 * same track to be wrong.
 *
 * Only the combat block below is new, and every number in it is server-computed —
 * ballistic and melee modifiers, effective armor, the evocation sums. The client adds
 * nothing.
 *
 * **The block is read through the encounter's lens.** A social encounter shows the skills
 * that carry a Social offence, defence or initiative where a combat one shows ballistic,
 * melee and armour — the same card, the numbers that are actually going to be rolled. Both
 * halves come down on every card, so re-typing an encounter switches this with no refetch.
 */
export function CombatCard({
  card,
  lens = 'Combat',
}: {
  card: EncounterCard
  lens?: EncounterType
}) {
  const combat = card.combat
  const shapes = card.shapes?.shapes ?? []
  const social = lens === 'Social'

  return (
    <Card
      variant="plain"
      label={card.name ?? 'Participant'}
      hint={card.isBeastForm === true ? 'Beast form' : String(card.type ?? '') || undefined}
    >
      <Person
        name={card.name ?? ''}
        portraitUrl={card.portraitUrl}
        meta={String(card.type ?? '')}
        {...(card.characterId !== undefined && { characterId: card.characterId })}
      />

      {combat !== undefined && (
        <div className={styles.combatGrid}>
          {social ? (
            // Conviction and Discipline stay in both lenses: they are Power and Control, and
            // magic is as available in a negotiation as in a brawl.
            (combat.socialSkills ?? []).map((skill) => (
              <CombatStat key={skill.skillId} label={skill.name ?? ''} value={skill.levelValue} />
            ))
          ) : (
            <>
              <CombatStat label="Ballistic" value={combat.ballisticModifier} />
              <CombatStat label="Melee" value={combat.meleeModifier} />
              <CombatStat label="Armor" value={combat.armorModifier} />
            </>
          )}
          <CombatStat label="Conviction" value={combat.convictionLevel} />
          <CombatStat label="Discipline" value={combat.disciplineLevel} />
        </div>
      )}

      {!social && (combat?.armor ?? []).length > 0 && (
        <div className={styles.subBlock}>
          <SectionLabel tone="muted" micro>
            Armor
          </SectionLabel>
          <div className={styles.subList}>
            {(combat?.armor ?? []).map((piece) => (
              <div key={piece.equipmentId} className={styles.subRow}>
                <span className={styles.rowName}>{piece.name}</span>
                {/* Effective armor is base power plus Athletics — worked out server-side,
                    and zero in beast form, which wears nothing. */}
                <span className={styles.combatValue}>{piece.effectiveArmor}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {(combat?.evocationModifiers ?? []).length > 0 && (
        <div className={styles.subBlock}>
          <SectionLabel tone="muted" micro>
            Evocation
          </SectionLabel>
          <div className={styles.subList}>
            {(combat?.evocationModifiers ?? []).map((modifier) => (
              <div key={String(modifier.element)} className={styles.subRow}>
                <span className={styles.rowName}>{String(modifier.element)}</span>
                <span className={styles.rowMeta}>
                  Power {modifier.power} · Control {modifier.control}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <SkillsCard skills={card.skills} />
      <HarmCard stress={card.stress} consequences={card.consequences} />
      <AbilitiesCard abilities={card.abilities} shapeId={card.shapeId ?? null} shapes={shapes} />

      {/* Null rather than empty for a character with no spellcasting ability, so this
          branches on presence exactly as the API intended. */}
      {card.specializations != null && (
        <SpecializationsCard specializations={card.specializations} />
      )}
      {card.focusItems != null && <FocusItemsCard focusItems={card.focusItems} />}
      {card.enchantedItems != null && <EnchantedItemsCard enchantedItems={card.enchantedItems} />}
      {card.roteSpells != null && <RoteSpellsCard roteSpells={card.roteSpells} />}

      {/* Beast form wears nothing; the server already zeroes the armor, and hiding the
          equipment card avoids showing a sword the character cannot hold. */}
      {card.isBeastForm !== true && <EquipmentCard equipment={card.equipment} />}
    </Card>
  )
}

function CombatStat({ label, value }: { label: string; value: number | undefined }) {
  return (
    <span className={styles.combatStat}>
      <SectionLabel tone="muted" micro>
        {label}
      </SectionLabel>
      <span className={styles.combatValue}>{value ?? 0}</span>
    </span>
  )
}
